import gql from "graphql-tag";

import {parse as parseMarkdown} from "@khanacademy/pure-markdown";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {
    ExtractPerseusStringsType,
    LintTranslationsFEType,
} from "./__graphql-types__/index.ts";
import type {LintTranslationsFE_lintTranslations as LintResult} from "./__graphql-types__/LintTranslationsFE.ts";

export interface TranslationLinterInput {
    /** Context of the translation*/
    context?: string | null | undefined;
    /** Filename of the translation.  No longer used*/
    filename?: string | null | undefined;
    /** Format of the translation.  No longer used.*/
    format?: string | null | undefined;
    /** Original string*/
    msgid: string;
    /** Translated string*/
    msgstr: string;
    /** Markdown-parsed AST of the Original String (json)*/
    parsedMsgid: string;
    /** Markdown-parsed AST of the Translated String (json)*/
    parsedMsgstr: string;
}

const EXTRACT_PERSEUS_STRINGS: GqlOperation<
    ExtractPerseusStringsType["response"],
    ExtractPerseusStringsType["variables"]
> = gqlOp(gql`
    query ExtractPerseusStrings($perseusJson: String!) {
        extractPerseusStrings(perseusJson: $perseusJson)
    }
`);

const LINT_TRANSLATIONS: GqlOperation<
    LintTranslationsFEType["response"],
    LintTranslationsFEType["variables"]
> = gqlOp(gql`
    query LintTranslationsFE($translations: [TranslationLinterInput!]!) {
        lintTranslations(translations: $translations) {
            status
            message
            correction
        }
    }
`);

// parseTranslationMarkdown will use the exact same Markdown parser that
// we use in Perseus.  The output is a JSONified AST that can be passed up
// to the backend for linting so that the backend isn't responsible for
// transforming the markdown.  This allows the FE and BE to be consistent with
// the markdown parser we use.
function parseTranslationMarkdown(text: string): string {
    return JSON.stringify(parseMarkdown(text));
}

export function createLintTranslationInput(
    source: string,
    translated: string,
    context: string,
    format?: string,
): TranslationLinterInput {
    const parsedMsgid = parseTranslationMarkdown(source);
    const parsedMsgstr = parseTranslationMarkdown(translated);

    return {
        msgid: source,
        msgstr: translated,
        parsedMsgid,
        parsedMsgstr,
        context,
        format,
    };
}

// This will process a Perseus section.  This is either single Article Section
// or an Exercise item in JSON form.
export async function lintPerseusData(
    gqlClient: ApolloGqlClient,
    perseusJSON: string,
): Promise<Array<LintResult>> {
    // NOTE(briang): These queries need to be called as "no-cache" because
    // Apollo gets confused when the query is large; likely because the data
    // doesn't have ids.

    const perseusStringsResult = await gqlClient.query(
        EXTRACT_PERSEUS_STRINGS,
        {
            variables: {
                perseusJson: perseusJSON,
            },
            fetchPolicy: "no-cache", // see note above
        },
    );

    if (!perseusStringsResult.data) {
        throw new Error("No data returned from EXTRACT_PERSEUS_STRINGS");
    }

    const strings = perseusStringsResult.data.extractPerseusStrings;
    const inputs = strings.map((msgId) =>
        createLintTranslationInput(
            msgId,
            msgId,
            "(format: perseus_text)",
            "perseus_text",
        ),
    );
    const linterResult = await gqlClient.query(LINT_TRANSLATIONS, {
        variables: {
            translations: inputs,
        },
        fetchPolicy: "no-cache", // see note above
    });

    if (!linterResult.data) {
        throw new Error("No data returned from LINT_TRANSLATIONS");
    }

    return linterResult.data.lintTranslations;
}
